// ── Vote-Kick Panel ── In-game overlay for team vote-kick UI
// Shows vote prompt with countdown timer, Yes/No buttons (F1/F2),
// live vote tally, and auto-hides on expiry.

const ACCENT = '#e94560';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';
const GOLD = '#e8b849';

type VoteResult = 'passed' | 'failed';

export interface VoteShowData {
  /** Header chip; defaults to "VOTE KICK" for the kick flow. */
  title?: string;
  initiator?: string;
  target?: string;
  /** Duration in seconds */
  duration: number;
  /** Per-show vote callback. Overrides the default registered via onVote(). */
  onVote?: (vote: boolean) => void;
  /**
   * U-M-006: per-show abstain callback. Fires when the user dismisses the
   * panel via ESC (or future X-button) without casting Yes/No. Overrides the
   * panel-wide callback registered via onAbstain().
   */
  onAbstain?: () => void;
}

export interface VoteTallyData {
  yes?: number;
  no?: number;
  abstain?: number;
  abstainCount?: number;
}

export class VotePanel {
  private overlay: HTMLElement;
  private contentEl: HTMLElement;
  private visible = false;

  // Internal elements
  private headerEl: HTMLElement | null = null;
  private timerBarFill: HTMLElement | null = null;
  private timerText: HTMLElement | null = null;
  private yesCountEl: HTMLElement | null = null;
  private noCountEl: HTMLElement | null = null;
  private abstainCountEl: HTMLElement | null = null;
  private voteHistoryEl: HTMLElement | null = null;
  private yesBtn: HTMLElement | null = null;
  private noBtn: HTMLElement | null = null;

  // State
  private timerId: ReturnType<typeof setInterval> | null = null;
  private remaining = 0;
  private duration = 0;
  private abstainCount = 0;
  // BUG-P-30: anchor the countdown to a wall-clock end time so the timer
  // text + bar stay accurate after the tab backgrounds (setInterval is
  // throttled to ~1Hz or paused entirely in hidden tabs, which would
  // otherwise let `remaining` drift behind real time).
  private targetEndTime = 0;
  private voteCallback: ((vote: boolean) => void) | null = null;
  private sessionVoteCallback: ((vote: boolean) => void) | null = null;
  // BUG-P-22: track whether the local player has already cast a vote in the
  // active session so the Yes/No buttons can be visually disabled and the
  // callback can be prevented from firing twice. Reset on hide()/show().
  private votedLocally = false;
  // U-M-006: abstain callback fires when ESC dismisses the panel without
  // casting a Yes/No vote. Per-show override mirrors voteCallback shape.
  private abstainCallback: (() => void) | null = null;
  private sessionAbstainCallback: (() => void) | null = null;
  private voteHistory: VoteResult[] = [];
  private boundKeyHandler = (e: KeyboardEvent) => this.handleKeyDown(e);

  constructor(private container: HTMLElement) {
    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.6)',
      zIndex: '70',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    this.contentEl = document.createElement('div');
    Object.assign(this.contentEl.style, {
      width: 'min(420px, calc(100vw - 24px))',
      border: `2px solid ${ACCENT}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.98)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    });
    this.overlay.appendChild(this.contentEl);

    document.body.appendChild(this.overlay);
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  /** Register a callback invoked when the local player votes. */
  onVote(callback: (vote: boolean) => void): void {
    this.voteCallback = callback;
  }

  /**
   * U-M-006: Register a callback invoked when the local player dismisses the
   * vote (via ESC) without casting Yes/No. Lets the caller forward an abstain
   * to the server instead of silently dropping the input.
   */
  onAbstain(callback: () => void): void {
    this.abstainCallback = callback;
  }

  show(data: VoteShowData): void {
    if (this.visible) this.hide();
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.duration = data.duration;
    this.remaining = data.duration;
    // BUG-P-30: capture the wall-clock deadline so tick reads from Date.now()
    // instead of decrementing a local counter that stalls when the tab is
    // backgrounded. duration is in seconds.
    this.targetEndTime = Date.now() + data.duration * 1000;
    this.sessionVoteCallback = data.onVote ?? null;
    this.sessionAbstainCallback = data.onAbstain ?? null;
    // BUG-P-22: clear any prior vote so the buttons re-enable for this session.
    this.votedLocally = false;
    this.abstainCount = 0;

    this.render(data);
    this.startTimer();
  }

  hide(): void {
    // BUG-29: Always stop timer first to prevent it firing after DOM removal
    this.stopTimer();
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
    this.sessionVoteCallback = null;
    this.sessionAbstainCallback = null;
    // BUG-P-22: reset for the next vote prompt.
    this.votedLocally = false;
  }

  isOpen(): boolean {
    return this.visible;
  }

  /** Update live vote tally display. */
  updateVotes(yes: number, no: number, abstainCount?: number): void;
  updateVotes(data: VoteTallyData): void;
  updateVotes(yesOrData: number | VoteTallyData, no = 0, abstainCount = 0): void {
    const yes = typeof yesOrData === 'number' ? yesOrData : yesOrData.yes ?? 0;
    const noVotes = typeof yesOrData === 'number' ? no : yesOrData.no ?? 0;
    const abstainVotes = typeof yesOrData === 'number'
      ? abstainCount
      : yesOrData.abstainCount ?? yesOrData.abstain ?? 0;

    if (this.yesCountEl) this.yesCountEl.textContent = String(yes);
    if (this.noCountEl) this.noCountEl.textContent = String(noVotes);
    this.abstainCount = Math.max(0, abstainVotes);
    this.updateAbstainDisplay();
  }

  recordVoteResult(result: VoteResult | boolean): void {
    const outcome = typeof result === 'boolean' ? (result ? 'passed' : 'failed') : result;
    this.voteHistory.push(outcome);
    this.voteHistory = this.voteHistory.slice(-3);
    this.updateVoteHistoryDisplay();
  }

  dispose(): void {
    this.stopTimer();
    document.removeEventListener('keydown', this.boundKeyHandler);
    this.overlay.remove();
  }

  // ── Internal ──

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.visible) return;
    if (e.key === 'F1') {
      e.preventDefault();
      this.castVote(true);
    } else if (e.key === 'F2') {
      e.preventDefault();
      this.castVote(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // U-M-006: dismissing the panel with ESC counts as an abstain — fire the
      // callback so callers can forward it to the server, then close the UI.
      // Order matters: fire BEFORE hide() because hide() clears the session
      // callback.
      this.castAbstain();
      this.hide();
    }
  }

  private castVote(vote: boolean): void {
    // BUG-P-22: ignore repeat votes within the same session — the server only
    // counts the first cast anyway, and double-firing the callback can confuse
    // the round/event manager.
    if (this.votedLocally) return;
    this.votedLocally = true;
    this.disableVoteButtons();
    (this.sessionVoteCallback ?? this.voteCallback)?.(vote);
  }

  /** BUG-P-22: visually disable both Yes/No buttons after a vote is cast. */
  private disableVoteButtons(): void {
    for (const btn of [this.yesBtn, this.noBtn]) {
      if (!btn) continue;
      const b = btn as HTMLButtonElement;
      b.disabled = true;
      // classList may be undefined under test mocks — guard before use.
      b.classList?.add('voted');
      b.style.opacity = '0.45';
      b.style.cursor = 'not-allowed';
      b.setAttribute('aria-disabled', 'true');
    }
  }

  /** U-M-006: fire the registered abstain callback (per-show overrides panel-wide). */
  private castAbstain(): void {
    (this.sessionAbstainCallback ?? this.abstainCallback)?.();
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerId = setInterval(() => {
      // BUG-P-30: read remaining time from the wall-clock deadline instead of
      // decrementing a counter. setInterval is throttled (and may be paused
      // entirely) in background tabs, so a -1 per tick approach drifts behind
      // real time. Math.ceil so the visible "Ns" doesn't tick to 0 a frame
      // before the deadline.
      const remainingMs = Math.max(0, this.targetEndTime - Date.now());
      this.remaining = Math.ceil(remainingMs / 1000);
      this.updateTimerDisplay();
      if (remainingMs <= 0) {
        this.hide();
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private updateTimerDisplay(): void {
    if (this.timerText) {
      this.timerText.textContent = `${this.remaining}s`;
    }
    if (this.timerBarFill && this.duration > 0) {
      const pct = (this.remaining / this.duration) * 100;
      this.timerBarFill.style.width = `${pct}%`;
    }
  }

  private updateAbstainDisplay(): void {
    if (!this.abstainCountEl) return;
    this.abstainCountEl.textContent = `[+${this.abstainCount} abstain]`;
    this.abstainCountEl.style.display = this.abstainCount > 0 ? 'inline-flex' : 'none';
  }

  private updateVoteHistoryDisplay(): void {
    if (!this.voteHistoryEl) return;
    this.voteHistoryEl.textContent = '';
    if (this.voteHistory.length === 0) {
      this.voteHistoryEl.style.display = 'none';
      return;
    }

    this.voteHistoryEl.style.display = 'flex';
    const label = document.createElement('span');
    label.textContent = 'Recent votes';
    Object.assign(label.style, {
      color: '#666',
      fontSize: '9px',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      marginRight: '4px',
    });
    this.voteHistoryEl.appendChild(label);

    for (const outcome of this.voteHistory) {
      const chip = document.createElement('span');
      chip.textContent = outcome;
      Object.assign(chip.style, {
        padding: '2px 6px',
        borderRadius: '3px',
        border: `1px solid ${outcome === 'passed' ? '#4caf5060' : '#ff525260'}`,
        color: outcome === 'passed' ? '#4caf50' : '#ff5252',
        background: outcome === 'passed' ? 'rgba(76,175,80,0.08)' : 'rgba(255,82,82,0.08)',
        fontSize: '9px',
        textTransform: 'uppercase',
      });
      this.voteHistoryEl.appendChild(chip);
    }
  }

  private render(data: VoteShowData): void {
    this.contentEl.innerHTML = '';

    // ── Header ──
    const header = document.createElement('div');
    Object.assign(header.style, {
      padding: '20px 24px 14px',
      borderBottom: `1px solid ${ACCENT}30`,
      textAlign: 'center',
      position: 'relative',
    });

    // BUG-P-14: small × close button top-right. Clicking it counts as an
    // abstain (same semantics as ESC) so the server isn't left waiting on a
    // dropped input.
    const closeBtn = document.createElement('button');
    closeBtn.setAttribute('data-role', 'vote-close');
    closeBtn.setAttribute('aria-label', 'Close vote');
    Object.assign(closeBtn.style, {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '24px',
      height: '24px',
      padding: '0',
      lineHeight: '20px',
      background: 'transparent',
      border: 'none',
      color: '#888',
      cursor: 'pointer',
      fontSize: '18px',
      fontFamily: FONT,
    });
    closeBtn.textContent = '×';
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.color = '#fff'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.color = '#888'; });
    closeBtn.addEventListener('click', () => {
      // Order matches the ESC handler: fire abstain BEFORE hide() because
      // hide() clears the session callback.
      this.castAbstain();
      this.hide();
    });
    header.appendChild(closeBtn);

    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, {
      fontSize: '18px',
      fontWeight: 'bold',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      color: ACCENT,
    });
    titleEl.textContent = (data.title ?? 'Vote Kick').toUpperCase();
    header.appendChild(titleEl);

    if (data.target) {
      const targetEl = document.createElement('div');
      Object.assign(targetEl.style, {
        fontSize: '15px',
        color: '#fff',
        marginTop: '8px',
      });
      targetEl.textContent = `Kick "${data.target}"?`;
      this.headerEl = targetEl;
      header.appendChild(targetEl);
    }

    if (data.initiator) {
      const initiatorEl = document.createElement('div');
      Object.assign(initiatorEl.style, {
        fontSize: '11px',
        color: '#666',
        marginTop: '4px',
        letterSpacing: '1px',
      });
      initiatorEl.textContent = `Called by ${data.initiator}`;
      header.appendChild(initiatorEl);
    }

    this.contentEl.appendChild(header);

    // ── Timer bar ──
    const timerContainer = document.createElement('div');
    Object.assign(timerContainer.style, {
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    });

    const barOuter = document.createElement('div');
    Object.assign(barOuter.style, {
      flex: '1',
      height: '8px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '4px',
      overflow: 'hidden',
    });

    const barFill = document.createElement('div');
    Object.assign(barFill.style, {
      width: '100%',
      height: '100%',
      background: ACCENT,
      borderRadius: '4px',
      transition: 'width 1s linear',
    });
    this.timerBarFill = barFill;
    barOuter.appendChild(barFill);
    timerContainer.appendChild(barOuter);

    const timerLabel = document.createElement('div');
    Object.assign(timerLabel.style, {
      fontSize: '14px',
      color: GOLD,
      fontWeight: 'bold',
      minWidth: '36px',
      textAlign: 'right',
    });
    timerLabel.textContent = `${this.remaining}s`;
    this.timerText = timerLabel;
    timerContainer.appendChild(timerLabel);

    this.contentEl.appendChild(timerContainer);

    // ── Vote tally ──
    const tallyRow = document.createElement('div');
    Object.assign(tallyRow.style, {
      display: 'flex',
      justifyContent: 'center',
      gap: '40px',
      padding: '8px 24px',
      fontSize: '13px',
    });

    const yesGroup = document.createElement('div');
    Object.assign(yesGroup.style, { textAlign: 'center' });
    const yesLabel = document.createElement('div');
    yesLabel.style.color = '#4caf50';
    yesLabel.style.fontSize = '10px';
    yesLabel.style.letterSpacing = '1px';
    yesLabel.textContent = 'YES';
    yesGroup.appendChild(yesLabel);
    const yesCount = document.createElement('div');
    yesCount.style.color = '#4caf50';
    yesCount.style.fontSize = '20px';
    yesCount.style.fontWeight = 'bold';
    yesCount.textContent = '0';
    this.yesCountEl = yesCount;
    yesGroup.appendChild(yesCount);
    tallyRow.appendChild(yesGroup);

    const noGroup = document.createElement('div');
    Object.assign(noGroup.style, { textAlign: 'center' });
    const noLabel = document.createElement('div');
    noLabel.style.color = '#ff5252';
    noLabel.style.fontSize = '10px';
    noLabel.style.letterSpacing = '1px';
    noLabel.textContent = 'NO';
    noGroup.appendChild(noLabel);
    const noCount = document.createElement('div');
    noCount.style.color = '#ff5252';
    noCount.style.fontSize = '20px';
    noCount.style.fontWeight = 'bold';
    noCount.textContent = '0';
    this.noCountEl = noCount;
    noGroup.appendChild(noCount);
    tallyRow.appendChild(noGroup);

    const abstainBadge = document.createElement('div');
    Object.assign(abstainBadge.style, {
      alignSelf: 'center',
      display: 'none',
      padding: '3px 8px',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: '3px',
      color: '#aaa',
      background: 'rgba(255,255,255,0.05)',
      fontSize: '10px',
      letterSpacing: '1px',
      opacity: '0.65',
      whiteSpace: 'nowrap',
    });
    this.abstainCountEl = abstainBadge;
    this.updateAbstainDisplay();
    tallyRow.appendChild(abstainBadge);

    this.contentEl.appendChild(tallyRow);

    // ── Buttons ──
    const btnRow = document.createElement('div');
    Object.assign(btnRow.style, {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      padding: '16px 24px 20px',
    });

    const yesBtn = this.createVoteBtn('YES', 'F1', '#4caf50', () => this.castVote(true));
    this.yesBtn = yesBtn;
    btnRow.appendChild(yesBtn);

    const noBtn = this.createVoteBtn('NO', 'F2', '#ff5252', () => this.castVote(false));
    this.noBtn = noBtn;
    btnRow.appendChild(noBtn);

    this.contentEl.appendChild(btnRow);

    // ── Vote history ──
    const history = document.createElement('div');
    Object.assign(history.style, {
      display: 'none',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '6px',
      padding: '0 24px 10px',
      flexWrap: 'wrap',
    });
    this.voteHistoryEl = history;
    this.contentEl.appendChild(history);
    this.updateVoteHistoryDisplay();

    // ── Footer ──
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      padding: '10px',
      textAlign: 'center',
      fontSize: '10px',
      color: '#444',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });
    footer.textContent = '[F1] Vote Yes  |  [F2] Vote No  |  [ESC] Close';
    this.contentEl.appendChild(footer);
  }

  private createVoteBtn(
    label: string,
    shortcut: string,
    color: string,
    onClick: () => void,
  ): HTMLElement {
    const btn = document.createElement('button');
    Object.assign(btn.style, {
      padding: '12px 28px',
      background: `${color}15`,
      border: `2px solid ${color}60`,
      borderRadius: '4px',
      color: color,
      cursor: 'pointer',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      fontSize: '14px',
      fontWeight: 'bold',
      fontFamily: FONT,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    });

    const labelSpan = document.createElement('span');
    labelSpan.textContent = label;
    btn.appendChild(labelSpan);

    const shortcutSpan = document.createElement('span');
    Object.assign(shortcutSpan.style, {
      fontSize: '10px',
      opacity: '0.6',
      padding: '2px 6px',
      border: `1px solid ${color}40`,
      borderRadius: '3px',
    });
    shortcutSpan.textContent = shortcut;
    btn.appendChild(shortcutSpan);

    btn.addEventListener('mouseenter', () => {
      btn.style.background = `${color}25`;
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = `${color}15`;
    });
    btn.addEventListener('click', onClick);

    return btn;
  }
}
